/**
 * Pengelly Garden Centre — Wishlist Page
 * Handles wishlist page rendering, remove buttons, and clear all.
 */
const PengellyWishlist = {
  STORAGE_KEY: 'pgc_wishlist',

  getItems() {
    try {
      return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
    } catch (e) {
      return [];
    }
  },

  saveItems(items) {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    // Notify wishlist-v2.js components (header badge, product buttons)
    document.dispatchEvent(new CustomEvent('wishlist:updated'));
  },

  addItem(product) {
    const items = this.getItems();
    if (!items.find(i => i.id === product.id)) {
      items.push(product);
      this.saveItems(items);
    }
    this.updateAllButtons();
    this.showToast(`"${product.title}" added to your wishlist`);
  },

  removeItem(productId) {
    const items = this.getItems().filter(i => i.id !== productId);
    this.saveItems(items);
    this.updateAllButtons();
    this.renderWishlistPage();
    this.showToast('Removed from your wishlist');
  },

  clearAll() {
    if (!confirm('Clear your entire wishlist?')) return;
    this.saveItems([]);
    this.updateAllButtons();
    this.renderWishlistPage();
    this.showToast('Wishlist cleared');
  },

  isInWishlist(productId) {
    return this.getItems().some(i => i.id === productId);
  },

  formatPrice(price) {
    // price stored in pence (e.g. 295 = £2.95)
    if (typeof price === 'number') {
      return '£' + (price / 100).toFixed(2);
    }
    // already formatted string
    return price;
  },

  updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const id = parseInt(btn.dataset.productId);
      const inList = this.isInWishlist(id);
      btn.classList.toggle('wishlist-btn--active', inList);
      btn.setAttribute('aria-label', inList ? 'Remove from wishlist' : 'Add to wishlist');
      btn.title = inList ? 'Remove from wishlist' : 'Add to wishlist';
      const label = btn.querySelector('.wishlist-btn__label');
      if (label) label.textContent = inList ? 'Saved to Wishlist' : 'Add to Wishlist';
    });
    this.updateHeaderCount();
  },

  updateHeaderCount() {
    const count = this.getItems().length;
    // PGC header badge
    const badge = document.querySelector('.wishlist-header-count');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }
    // Header icon filled state
    const headerIcon = document.querySelector('.header__icon--wishlist');
    if (headerIcon) {
      headerIcon.classList.toggle('wishlist-has-items', count > 0);
    }
  },

  showToast(message) {
    let toast = document.getElementById('pgc-wishlist-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'pgc-wishlist-toast';
      toast.style.cssText = `
        position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%);
        background: #9d3572; color: #fff; padding: 12px 24px; border-radius: 6px;
        font-size: 14px; font-weight: 500; z-index: 9999; opacity: 0;
        transition: opacity 0.3s ease; pointer-events: none; white-space: nowrap;
        box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      `;
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.opacity = '1';
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => { toast.style.opacity = '0'; }, 3000);
  },

  renderWishlistPage() {
    const container = document.getElementById('pgc-wishlist-container');
    if (!container) return;

    const items = this.getItems();
    const empty = document.getElementById('pgc-wishlist-empty');
    const grid = document.getElementById('pgc-wishlist-grid');
    const footer = document.getElementById('pgc-wishlist-footer');

    if (items.length === 0) {
      if (empty) empty.style.display = 'block';
      if (grid) grid.innerHTML = '';
      if (footer) footer.style.display = 'none';
      return;
    }

    if (empty) empty.style.display = 'none';
    if (footer) footer.style.display = 'block';
    if (!grid) return;

    grid.innerHTML = items.map(item => `
      <div class="pgc-wishlist-card" data-product-id="${item.id}">
        <a href="${item.url}" class="pgc-wishlist-card__image-link">
          <img src="${item.image}" alt="${item.title}" loading="lazy" width="300" height="300">
        </a>
        <div class="pgc-wishlist-card__body">
          <a href="${item.url}" class="pgc-wishlist-card__title">${item.title}</a>
          <p class="pgc-wishlist-card__price">${this.formatPrice(item.price)}</p>
          <div class="pgc-wishlist-card__actions">
            <a href="${item.url}" class="pgc-wishlist-card__btn pgc-wishlist-card__btn--primary">
              View Product
            </a>
            <button
              class="pgc-wishlist-card__btn pgc-wishlist-card__btn--remove"
              onclick="PengellyWishlist.removeItem(${item.id})"
              aria-label="Remove from wishlist"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              Remove
            </button>
          </div>
        </div>
      </div>
    `).join('');
  },

  init() {
    // Attach click handlers to all wishlist buttons on the page
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const id = parseInt(btn.dataset.productId);
        if (this.isInWishlist(id)) {
          this.removeItem(id);
        } else {
          this.addItem({
            id: id,
            title: btn.dataset.productTitle,
            price: btn.dataset.productPrice,
            image: btn.dataset.productImage,
            url: btn.dataset.productUrl,
          });
        }
      });
    });

    // Wire up clear all button
    const clearBtn = document.getElementById('pgc-wishlist-clear-btn');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearAll());
    }

    this.updateAllButtons();
    this.renderWishlistPage();
  }
};

document.addEventListener('DOMContentLoaded', () => PengellyWishlist.init());
